/**
 * Semrush Unit Budget Tracking + Circuit-Breaker (Story 20.4, AC #8)
 *
 * Semrush charges per-row for most reports + fixed-cost-per-request for
 * Backlinks (40u) + 50u/row for `phrase_kdi`. A sloppy widget config can
 * burn a customer's monthly quota in minutes.
 *
 * **Two separate quotas, two separate counters:**
 *
 *  1. **Analytics units** — `semrush:units_used:tenant:{tenantId}:{YYYY-MM-DD}`
 *     — daily roll-up, 60-day TTL. Per-tenant 200K/day default cap (a
 *     defensive default for a 5-client agency on a 1M-units/month plan;
 *     burning the monthly cap in 5 days is the typical onboarding mistake).
 *
 *  2. **Trends units** — `semrush:trends_units_used:integration:{integrationId}:{YYYY-MM}`
 *     — MONTHLY roll-up, 60-day TTL. Trends API has its own 10K-units/month
 *     basic-tier quota (per semrush.com/kb/930-trends-api). Hitting the cap
 *     returns `rate_limited` retryable=false with "resumes next month" copy.
 *
 * **The 60-day TTL on these KV keys is intentional and NOT subject to the
 * Semrush 30-day ToS §3.3 data-persistence cap** (per Story 20.4 AC #16) —
 * these are OBSERVABILITY records about our own system (units consumed by
 * our calls), not Semrush API response data.
 *
 * Why per-tenant for Analytics, per-integration for Trends: Analytics units
 * are workspace-shared (a multi-client agency may have many Semrush
 * integrations sharing the same plan quota). Trends units are charged per
 * the customer's `.Trends` add-on subscription, but the API surface tracks
 * per-key consumption. Per-integration is fine for Trends because each
 * integration row pins to one Semrush API key.
 */

import type { KVStore } from "../cache";

// ============================================================================
// Constants (AC #8)
// ============================================================================

/**
 * Per-tenant daily Analytics-unit budget. Calls are rejected with
 * `rate_limited` once the day's accumulated consumption crosses this
 * threshold.
 *
 * Sized at 200K/day = 1M/month÷5 days — catches the typical "one client
 * burned the monthly quota in a week" onboarding mistake. Easy to tune via
 * env override later.
 */
export const SEMRUSH_DAILY_TENANT_UNIT_BUDGET = 200_000;

/**
 * Per-integration MONTHLY Trends-unit quota. Trends API has its own 10K
 * units/month basic-tier quota per semrush.com/kb/930-trends-api 2026-05-08.
 *
 * Note: this is NOT a daily cap because the Trends quota is monthly. The KV
 * key is bucketed by `YYYY-MM` (not `YYYY-MM-DD`) so the cap resets at the
 * start of each calendar month UTC.
 */
export const SEMRUSH_MONTHLY_TRENDS_UNIT_BUDGET = 10_000;

/** KV TTL for unit-budget roll-up keys (60 days). */
export const SEMRUSH_UNITS_KV_TTL_SEC = 60 * 24 * 3600;

// ============================================================================
// Hard ceiling on `display_limit` (AC #4)
// ============================================================================

/**
 * Maximum `display_limit` allowed on a single list-style call. Semrush's API
 * permits up to 4,000,000 rows per call but a sloppy widget config could
 * burn 40,000+ units in a single call. Round 1 SRE finding: clamp at the
 * helper level.
 */
export const SEMRUSH_DISPLAY_LIMIT_MAX = 1000;

// ============================================================================
// Key construction
// ============================================================================

export function buildSemrushTenantUnitsKey(
  tenantId: string,
  yyyyMmDd: string
): string {
  return `semrush:units_used:tenant:${tenantId}:${yyyyMmDd}`;
}

export function buildSemrushIntegrationUnitsKey(
  integrationId: string,
  yyyyMmDd: string
): string {
  return `semrush:units_used:integration:${integrationId}:${yyyyMmDd}`;
}

export function buildSemrushTrendsUnitsKey(
  integrationId: string,
  yyyyMm: string
): string {
  return `semrush:trends_units_used:integration:${integrationId}:${yyyyMm}`;
}

export function buildSemrushTenantBreachMarkerKey(
  tenantId: string,
  yyyyMmDd: string
): string {
  return `semrush:budget_breach:${tenantId}:${yyyyMmDd}`;
}

/** Today's date in `YYYY-MM-DD` UTC form. */
export function todayUtcYyyyMmDd(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** This month in `YYYY-MM` UTC form (for Trends monthly bucket). */
export function thisMonthUtcYyyyMm(now: Date = new Date()): string {
  return now.toISOString().slice(0, 7);
}

// ============================================================================
// KV roll-up helpers
// ============================================================================

/**
 * Increment per-integration AND per-tenant daily Analytics-unit counters by
 * `units`. Both share the 60-day TTL so the integration card UI can show
 * "units used today" and "units used this month" without separate jobs.
 *
 * NOTE: KV does not support atomic increment. Under concurrent calls, two
 * workers can read the same value, increment, and write back — losing one
 * increment. This is the same trade-off PageSpeed + Ahrefs accept; the
 * Semrush server-side quota is the hard cap, our budget is a soft safety net.
 *
 * @returns the post-increment per-tenant total (for circuit-breaker checks).
 */
export async function incrementSemrushUnitsUsed(
  kv: KVStore,
  tenantId: string,
  integrationId: string,
  units: number,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  if (!Number.isFinite(units) || units <= 0) {
    return 0;
  }
  const tenantKey = buildSemrushTenantUnitsKey(tenantId, date);
  const integrationKey = buildSemrushIntegrationUnitsKey(integrationId, date);

  const [tenantRaw, integrationRaw] = await Promise.all([
    kv.get(tenantKey),
    kv.get(integrationKey),
  ]);

  const tenantTotal = (Number(tenantRaw ?? "0") || 0) + units;
  const integrationTotal = (Number(integrationRaw ?? "0") || 0) + units;

  await Promise.all([
    kv.put(tenantKey, String(tenantTotal), { expirationTtl: SEMRUSH_UNITS_KV_TTL_SEC }),
    kv.put(integrationKey, String(integrationTotal), {
      expirationTtl: SEMRUSH_UNITS_KV_TTL_SEC,
    }),
  ]);
  return tenantTotal;
}

/**
 * Increment the per-integration MONTHLY Trends-unit counter by `units`.
 *
 * @returns the post-increment Trends total (for monthly-cap checks).
 */
export async function incrementSemrushTrendsUnitsUsed(
  kv: KVStore,
  integrationId: string,
  units: number,
  month: string = thisMonthUtcYyyyMm()
): Promise<number> {
  if (!Number.isFinite(units) || units <= 0) {
    return 0;
  }
  const key = buildSemrushTrendsUnitsKey(integrationId, month);
  const raw = await kv.get(key);
  const total = (Number(raw ?? "0") || 0) + units;
  await kv.put(key, String(total), { expirationTtl: SEMRUSH_UNITS_KV_TTL_SEC });
  return total;
}

export async function getSemrushTenantDailyUnits(
  kv: KVStore,
  tenantId: string,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  const raw = await kv.get(buildSemrushTenantUnitsKey(tenantId, date));
  return Number(raw ?? "0") || 0;
}

export async function getSemrushIntegrationDailyUnits(
  kv: KVStore,
  integrationId: string,
  date: string = todayUtcYyyyMmDd()
): Promise<number> {
  const raw = await kv.get(buildSemrushIntegrationUnitsKey(integrationId, date));
  return Number(raw ?? "0") || 0;
}

export async function getSemrushTrendsMonthlyUnits(
  kv: KVStore,
  integrationId: string,
  month: string = thisMonthUtcYyyyMm()
): Promise<number> {
  const raw = await kv.get(buildSemrushTrendsUnitsKey(integrationId, month));
  return Number(raw ?? "0") || 0;
}

// ============================================================================
// Circuit-breaker
// ============================================================================

export interface SemrushBudgetCheckResult {
  readonly exceeded: boolean;
  readonly current: number;
  readonly limit: number;
  readonly firstBreach: boolean;
}

/**
 * Per-tenant Analytics-unit circuit-breaker. Mirrors
 * `checkAhrefsUnitBudget` semantics — when the cap is crossed for the first
 * time today, a marker key is set so subsequent calls return
 * `firstBreach: false` (caller decides whether to fire the per-occurrence
 * notification).
 */
export async function checkSemrushUnitBudget(
  kv: KVStore,
  tenantId: string,
  limit: number = SEMRUSH_DAILY_TENANT_UNIT_BUDGET,
  date: string = todayUtcYyyyMmDd()
): Promise<SemrushBudgetCheckResult> {
  const current = await getSemrushTenantDailyUnits(kv, tenantId, date);
  const exceeded = current >= limit;
  if (!exceeded) {
    return { exceeded: false, current, limit, firstBreach: false };
  }
  const markerKey = buildSemrushTenantBreachMarkerKey(tenantId, date);
  const markerRaw = await kv.get(markerKey);
  const firstBreach = markerRaw === null;
  if (firstBreach) {
    await kv.put(markerKey, String(Date.now()), { expirationTtl: 24 * 3600 });
  }
  return { exceeded: true, current, limit, firstBreach };
}

/**
 * Per-integration MONTHLY Trends-unit circuit-breaker. Mirrors
 * `checkSemrushUnitBudget` shape but bucketed monthly.
 */
export async function checkSemrushTrendsUnitBudget(
  kv: KVStore,
  integrationId: string,
  limit: number = SEMRUSH_MONTHLY_TRENDS_UNIT_BUDGET,
  month: string = thisMonthUtcYyyyMm()
): Promise<SemrushBudgetCheckResult> {
  const current = await getSemrushTrendsMonthlyUnits(kv, integrationId, month);
  const exceeded = current >= limit;
  return { exceeded, current, limit, firstBreach: false };
}
